import { plural, t } from '@lingui/macro';

import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { formatCurrency } from '@rover/utilities/currency';
import { getIsPriceTransparencyFull } from '@rover/utilities/priceTransparency';

export type PriceDisplayProps = {
  currencyCode: string;
  priceTransparencyLevel?: PriceTransparencyLevel;
  language: string;
  price: string;
  priceInCredits?: number | null;
  shouldShowPriceInCredits?: boolean;
  showFromText?: boolean;
  unitAsSentence: string;
  isTrainingDesign?: boolean;
  isBHUser?: boolean;
};

type PriceDisplayInfo = {
  formattedPrice: string;
  beforePriceText: string;
  afterPriceText: string;
  showBrightHorizonsCalloutBadge: boolean;
};

export default function usePriceDisplayInfo({
  currencyCode,
  priceTransparencyLevel,
  language,
  price,
  priceInCredits = null,
  shouldShowPriceInCredits = false,
  showFromText = true,
  unitAsSentence,
  isTrainingDesign = false,
  isBHUser = false,
}: PriceDisplayProps): PriceDisplayInfo {
  let formattedPrice = '';
  if (shouldShowPriceInCredits && priceInCredits !== null) {
    formattedPrice = plural(priceInCredits, {
      one: '# credit',
      other: '# credits',
    });
  } else {
    formattedPrice = formatCurrency(price, currencyCode, language, {
      includeDecimal: false,
    });
  }

  const isPtFull = getIsPriceTransparencyFull(priceTransparencyLevel);
  const showFromTextLabel = isBHUser ? showFromText : !isPtFull && !isTrainingDesign;

  const beforePriceText = showFromTextLabel ? t`from` : '';
  const afterPriceText =
    (!showFromText && isBHUser) || isPtFull ? t`total ${unitAsSentence}` : unitAsSentence;

  const showBrightHorizonsCalloutBadge =
    isBHUser && !shouldShowPriceInCredits && priceInCredits === null;

  return {
    formattedPrice,
    beforePriceText,
    afterPriceText,
    showBrightHorizonsCalloutBadge,
  };
}
